import React, { forwardRef, useRef } from "react";
import { faCaretDown, faCaretUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const SubtotalSection = forwardRef(
  (
    {
      multiplier,
      overallTotal,
      isRawType,
      totalPacket,
      commission,
      setCommission,
      practitionerCommission,
      prepMethod,
      setPrepMethod,
      totalValue,
      setTotalValue,
      prepValue,
      setPrepValue,
      preparationMethods,
      maximumGramForCreation,
    },
    ref
  ) => {
    const [commissionType, setCommissionType] = useState(0);

    useEffect(() => {
      let commissionValue = isRawType
        ? (overallTotal.totalPrice * totalPacket * (commission / 100)).toFixed(
            2
          )
        : (overallTotal.totalPrice * (commission / 100)).toFixed(2);
      let prepValue = getPreparationValue(prepMethod);

      setPrepValue(prepValue);
      setTotalValue(
        (isRawType
          ? parseFloat(overallTotal.totalPrice * totalPacket)
          : parseFloat(overallTotal.totalPrice)) +
          parseFloat(commissionValue) +
          parseFloat(prepValue)
      );
    }, [commission, overallTotal, totalPacket, prepMethod, multiplier]);

    const getPreparationValue = (prepMethod) => {
      let prepValue = 0;
      if (prepMethod) {
        if (!prepMethod?.isRaw) {
          const weight = overallTotal.totalDosage;
          const nearestLowerMultiple =
            Math.floor(weight / parseFloat(prepMethod.multiple)) *
            parseFloat(prepMethod.multiple);
          const excess = weight - nearestLowerMultiple;
          let adjustedWeight;

          if (excess <= parseFloat(maximumGramForCreation)) {
            adjustedWeight = weight - excess;
          } else {
            adjustedWeight = weight;
          }
          adjustedWeight = adjustedWeight == 0 ? weight : adjustedWeight;
          prepValue =
            Math.ceil(adjustedWeight / parseFloat(prepMethod.perUnitQuantity)) *
            parseFloat(prepMethod.preparationValue);
        } else {
          prepValue = parseFloat(prepMethod.preparationValue) * totalPacket;
        }
      }
      return prepValue;
    };

    useEffect(() => {
      if (practitionerCommission) {
        if (commission >= 0) {
          setCommissionType(1);
        } else {
          setCommission(practitionerCommission);
          setTimeout(() => {
            setCommissionType(0);
          }, [100]);
        }
      } else {
        setCommissionType(1);
      }
    }, [practitionerCommission, commission]);

    return (
      <div className="subtotal-section">
        <hr />
        <table className="table">
          <tbody>
            <tr>
              <td width="45%">
                <b>Subtotal</b>
              </td>
              <td width="30%"></td>
              <td width="20%" className="text-right">
                <label className="mr-5">
                  {isRawType
                    ? (overallTotal.totalPrice * totalPacket).toLocaleString(
                        "en-US",
                        {
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                          style: "currency",
                          currency: "USD",
                        }
                      )
                    : overallTotal.totalPrice.toLocaleString("en-US", {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                        style: "currency",
                        currency: "USD",
                      })}
                </label>
              </td>
            </tr>
            <tr>
              <td width="45%">
                <b>Commission*</b>
              </td>
              <td
                className="stepper prep-method"
                style={{ gap: "20px" }}
                width="30%"
              >
                <div>
                  {practitionerCommission != null && (
                    <div className="prep-method">
                      <OverlayTrigger
                        placement="right"
                        delay={{ show: 200, hide: 100 }}
                        overlay={
                          <Tooltip id="commission-tooltip">
                            You have the option to personalize your default
                            commission percentage right from your profile page.
                          </Tooltip>
                        }
                      >
                        <label>
                          Your Default ({practitionerCommission}%){" "}
                          <input
                            className="ml-3"
                            type="radio"
                            name="commission"
                            onChange={() => {
                              setCommission(practitionerCommission);
                              setTimeout(() => {
                                setCommissionType(0);
                              }, [100]);
                            }}
                            checked={commissionType == 0}
                          />
                        </label>
                      </OverlayTrigger>
                    </div>
                  )}
                  <div className="prep-method ">
                    <label className={`d-flex `}>
                      Specify
                      <div className={`number-field ml-2 mr-1`}>
                        <input
                          type="text"
                          pattern="[0-9]*"
                          min={0}
                          value={commission}
                          onChange={(e) => {
                            parseInt(e.target.value) > 0
                              ? setCommission(parseInt(e.target.value))
                              : setCommission(0);
                          }}
                        />
                        <div>
                          <div
                            className="icon"
                            onClick={() => setCommission(commission + 1)}
                          >
                            <FontAwesomeIcon icon={faCaretUp} />
                          </div>
                          <div
                            className="icon"
                            onClick={() =>
                              setCommission(commission > 0 ? commission - 1 : 0)
                            }
                          >
                            <FontAwesomeIcon icon={faCaretDown} />
                          </div>
                        </div>
                      </div>{" "}
                      %
                      {practitionerCommission != null && (
                        <input
                          checked={commissionType == 1}
                          onChange={() => setCommissionType(1)}
                          type="radio"
                          name="commission"
                          className="ml-3"
                        />
                      )}
                    </label>
                  </div>
                </div>
              </td>
              <td width="20%" className="text-right">
                <label className="mr-5">
                  {isRawType
                    ? (
                        overallTotal.totalPrice *
                        totalPacket *
                        (commission / 100)
                      ).toLocaleString("en-US", {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                        style: "currency",
                        currency: "USD",
                      })
                    : (
                        overallTotal.totalPrice *
                        (commission / 100)
                      ).toLocaleString("en-US", {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                        style: "currency",
                        currency: "USD",
                      })}
                </label>
              </td>
            </tr>

            <tr ref={ref}>
              <td width="45%">
                <b>Preparation Method*</b>
              </td>
              <td width="30%" className="prep-method">
                <label>
                  <>
                    <div className="prep-method">
                      {preparationMethods
                        ?.filter((x) => x.isRaw == isRawType && x.status)
                        .map((x) => {
                          return (
                            <div key={x.id}>
                              <label>
                                {x.prepMethod}{" "}
                                {getPreparationValue(x) ? (
                                  <>
                                    (
                                    {getPreparationValue(x)?.toLocaleString(
                                      "en-US",
                                      {
                                        maximumFractionDigits: 2,
                                        minimumFractionDigits: 2,
                                        style: "currency",
                                        currency: "USD",
                                      }
                                    )}
                                    )
                                  </>
                                ) : (
                                  ""
                                )}
                                <input
                                  style={{ marginLeft: "10px" }}
                                  type="radio"
                                  checked={prepMethod?.id == x.id}
                                  onChange={() => {
                                    setPrepMethod(x);
                                    if (ref) {
                                      ref.current.style.border =
                                        "1px solid transparent";
                                    }
                                  }}
                                  name="prepMethod"
                                />
                              </label>
                              <br />
                            </div>
                          );
                        })}
                    </div>
                  </>
                </label>
              </td>

              <td width="20%" className="text-right">
                <label className="mr-5">
                  {prepValue?.toLocaleString("en-US", {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                    style: "currency",
                    currency: "USD",
                  })}
                </label>
              </td>
            </tr>
            <tr>
              <td width="45%"></td>
              <td width="30%"></td>
              <td width="20%" className="text-center">
                <hr style={{ backgroundColor: "#eee", width: "100%" }} />
              </td>
            </tr>
            <tr>
              <td width="45%">
                <b>Total</b>
              </td>
              <td width="30%"></td>
              <td width="20%" className="text-right">
                <b className="mr-5">
                  {totalValue?.toLocaleString("en-US", {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                    style: "currency",
                    currency: "USD",
                  })}
                </b>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
);

export default SubtotalSection;
